import { Injector } from '@angular/core';
import { FormActionTypeEnum, SEPAPaymentRailTypeEnum } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  ActionButtonOutcomesType,
  BaseWidgetProperties,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { UpdateFormDataAction } from '../../../actions/form-submission.action';
import { CreatePayment } from '../../../actions/payment.action';
import { TransferMoneyRequest, paymentRailOptions } from '../../../services/account-service/account.type';
import { AccountState } from '../../../state/account.state';
import { FormActionState } from '../../../state/form-submision.state';
import { formatSchedulePaymentDate } from '../cta-actions/unified-payments/payment-utils';
import { handleRedirection } from '../cta-redirection.utils';

/**
 * Submits a payment initiation request using the provided injector and widget properties.
 *
 * This function retrieves necessary services and state data using the injector,
 * constructs a payment request payload, and dispatches actions to update form data
 * and create a payment. If the request data is valid, it navigates to the payment
 * processing page. Otherwise, it shows an error alert.
 *
 * @param injector - The Angular injector used to retrieve services.
 * @param widgetProperties - The properties of the widget, including navigation details.
 */
export function submitInitiatorPayment(injector: Injector, widgetProperties: BaseWidgetProperties) {
  const store = injector.get(Store);
  const alertHandlerService = injector.get(AlertHandlerService);
  const requestData = store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData;
  const activeAccountId = store.selectSnapshot(AccountState.getCurrentAccountId);

  const sepaFormState = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.SEPA_PAYMENT),
  )?.formData;

  const schedulePaymentDate = formatSchedulePaymentDate(injector);

  if (!widgetProperties['zoneToNavigate']) {
    widgetProperties['zoneToNavigate'] = APP_ZONES.PAYMENT;
  }
  widgetProperties['urlToNavigate'] = `payment-processing-initiator`;

  if (requestData && Object.keys(requestData).length) {
    const payload: TransferMoneyRequest = {
      sourceAccountId: activeAccountId,
      bid: requestData.bid,
      amount: requestData.amount,
      paymentReference: requestData.paymentReference,
      transferReasonId: requestData.transferReasonId,
      paymentRailOption: sepaFormState?.paymentRail as SEPAPaymentRailTypeEnum | undefined,
      scheme: sepaFormState?.paymentRail
        ? paymentRailOptions[sepaFormState.paymentRail as SEPAPaymentRailTypeEnum]
        : undefined,
      schedulePaymentDate,
    };
    store.dispatch(
      new UpdateFormDataAction({
        transferDate: `${new Date()}`,
      }),
    );
    store.dispatch(new CreatePayment(payload));
    handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.PROCESSING, true);
  } else {
    alertHandlerService.showAlertFn('error', 'Payment request data not available, please try again');
    console.error('payment request data not available, please try again');
  }
}
